import { useTranslation } from 'react-i18next'

import { useDoorClipLoaderCameraNameQuery } from '@/graphql/generated/hooks'
import { DeviceType } from '@/graphql/generated/schemas'

interface UseCameraNameIProps {
  deviceId: string | undefined
  deviceName: string | undefined
  deviceType: DeviceType | undefined
  /**
   * Whether the ClipLoader will actually render. The door -> camera lookup is
   * a backend round-trip on the incident-open path, so callers must only turn
   * it on when the name is about to be shown (ENG-31021).
   */
  enabled: boolean
}

/**
 * Returns the camera name for ClipLoader display.
 *
 * - If device is a Camera: returns device name directly
 * - If device is a Door: fetches and returns the associated camera name
 * - Otherwise: returns the translated camera fallback label
 */
export const useCameraName = ({
  deviceId,
  deviceName,
  deviceType,
  enabled,
}: UseCameraNameIProps): string => {
  const { t } = useTranslation()
  const fallbackName = t('incidents:clip_loader_camera_fallback')

  // Only query if the ClipLoader is visible and the device is a Door
  const shouldQuery =
    enabled && deviceType === DeviceType.Door && Boolean(deviceId)

  const { data } = useDoorClipLoaderCameraNameQuery({
    variables: { id: deviceId ?? '' },
    skip: !shouldQuery,
  })

  // If Camera type, use device name directly
  if (deviceType === DeviceType.Camera) {
    return deviceName ?? fallbackName
  }

  // If Door type, get camera name from tailgateGrids
  if (deviceType === DeviceType.Door) {
    const cameraName = data?.device?.door?.tailgateGrids?.[0]?.camera?.name
    return cameraName ?? fallbackName
  }

  // Fallback for other device types
  return fallbackName
}
